import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { updateExternalLinks } from '../../../utils/context/utils';
import CtaButton from '../../sharedComponents/CtaButton';
import LazyloadImage from '../../sharedComponents/LazyloadImage';
import { sanitizeHTML } from '../utils';
import './feature.scss';

const featureCardClasses = classNames(
    'eds-g-cell',
    'eds-g-cell-sm-1-1',
    'eds-g-cell-sw-1-3',
    'eds-l-pad-all-6',
    'eds-g-cell--has-overflow',
);
const featureHeaderClasses = classNames(
    'eds-g-cell',
    'eds-g-cell-12-12',
    'eds-g-offset-0-12',
    'eds-g-cell-md-8-12',
    'eds-g-offset-md-2-12',
    'eds-g-cell-lg-6-12',
    'eds-g-offset-lg-3-12',
    'eds-text--center',
);
const featureHeadlineClasses = classNames(
    'eds-text-color--grey-900',
    'eds-text-hm',
    'eds-text--center',
    'mktg-text-weight--heavy',
);
const featureDescriptionClasses = classNames(
    'feature-module-description',
    'eds-text-color--grey-700',
    'eds-text-bl',
    'eds-text-weight--regular',
    'eds-l-mar-top-4',
    'eds-text--center eds-l-pad-bot-8',
);

interface FeatureCardInterface {
    alt?: string;
    header: string;
    subheader: string;
    description: string;
    icon: string;
}

export interface FeatureProps {
    headline: string;
    description: string;
    cards: FeatureCardInterface[];
    buttonText: string;
    buttonUrl: string;
    moduleClass?: string;
}

const FeatureCard = ({
    header,
    subheader,
    description,
    icon,
    alt,
}: FeatureCardInterface) => {
    const [updatedHtmlContent, setUpdatedHtmlContent] = useState(description);

    useEffect(() => {
        const updatedHTMLContent = updateExternalLinks(description);
        setUpdatedHtmlContent(updatedHTMLContent);
    }, [description]);

    return (
        <div className={featureCardClasses}>
            <div className="eds-l-pad-bot-8">
                <div className="mktg-shadow-secondary">
                    <LazyloadImage
                        image={icon}
                        alt={alt || header}
                        classes="g-img"
                    />
                </div>
            </div>
            <div className="eds-l-pad-hor-6 eds-l-pad-bot-2">
                <h2 className="eds-text-hl eds-l-pad-bot-2  eds-text-color--ui-orange">
                    {header}
                </h2>
                <p className="eds-text-bl eds-text-weight--heavy eds-text-color--grey-900 eds-l-pad-bot-2">
                    {subheader}
                </p>
                <div
                    className="eds-text-bm eds-text-color--grey-700 "
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                        __html: sanitizeHTML(updatedHtmlContent),
                    }}
                />
            </div>
        </div>
    );
};

const FeatureModule = ({
    buttonText,
    buttonUrl,
    headline,
    description,
    cards,
    moduleClass,
}: FeatureProps) => {
    const shouldIncludeCtaButton = buttonText && buttonUrl;
    return (
        <section className={`feature-module ${moduleClass}`}>
            <div className="marketing-container__max-width eds-l-pad-hor-6">
                <div className="eds-g-grid">
                    <div className="mktg-top-p-100">
                        <div className={featureHeaderClasses}>
                            <h2 className={featureHeadlineClasses}>
                                {headline}
                            </h2>
                            <div
                                className={featureDescriptionClasses}
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{
                                    __html: description.toString(),
                                }}
                            />
                        </div>
                        <div className="eds-g-group eds-g-cell-12-12 ">
                            {cards.map((card, index) => (
                                <FeatureCard key={index} {...card} />
                            ))}
                        </div>
                    </div>
                    <div className="eds-text--center eds-l-mar-bot-6">
                        {shouldIncludeCtaButton && (
                            <CtaButton
                                buttonText={buttonText}
                                buttonUrl={buttonUrl}
                                outerComponent="feature_module"
                            />
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default FeatureModule;
